import {action, observable, runInAction, makeObservable} from 'mobx';
import {decorate, inject, injectable} from '@yosmanyga/di';
import {Dispatcher} from '@yosmanyga/base';
import {PlayApi} from '../../data';

class PlayInputController {
    // User won?
    @observable accessor won: boolean | undefined;    // User dead?
    @observable accessor dead: boolean | undefined;

    @observable accessor input: string;
    @observable accessor focus: number;
    // Command history
    @observable accessor history: {
        index: number;
        values: Array<string>;
    };
    @observable accessor executing: boolean;

    constructor(
        private dispatcher: Dispatcher,
        private playApi: PlayApi
    ) {
        this.won = false;
        this.dead = false;
        this.input = '';
        this.focus = 1;
        this.history = {
            index: 0, // One ahead
            values: [],
        };
        this.executing = false;

        this.dispatcher.register('play_input_note_done', this.listenToPlayInputNoteDone);
    }

    @action prepare = (value: string) => {
        this.input = value;
    };

    @action reuse = (dir: number) => {
        if (
            // Trying to go back and already at the limit?
            (dir === -1 && this.history.index === 0) ||
            // Trying to go forward and already off the limit?
            (dir === 1 && this.history.index === this.history.values.length)
        ) {
            return;
        }

        this.history = {
            ...this.history,
            index: this.history.index + dir,
        };

        // Off the limit?
        if (this.history.index === this.history.values.length) {
            this.prepare('');

            return;
        }

        this.prepare(this.history.values[this.history.index]);
    };

    @action execute = async () => {
        const input = this.input;

        runInAction(() => {
            this.executing = true;
            this.input = '';
        });

        const {lines, version, won, dead} = await this.playApi.playGame(input);

        await Promise.all(
            this.dispatcher
                .retrieve<({input, lines, version}: { input: string, lines: string[], version: string }) => void>(
                    'play_input_executed'
                )
                .map(async (service) => {
                    service({input, lines, version});
                })
        );

        runInAction(() => {
            this.history = {
                index: this.history.index + 1,
                values: this.history.values.concat(input),
            };

            this.won = won;
            this.dead = dead;
            this.executing = false;
        });
    };

    @action listenToPlayInputNoteDone = () => {
        this.focus = this.focus + 1;
    }
}

decorate(injectable(), PlayInputController);
decorate(inject(Dispatcher), PlayInputController, 0);
decorate(inject(PlayApi), PlayInputController, 1);

export {PlayInputController};
